import React, { useCallback, useMemo } from 'react';
import { Form, Input, Modal, Button, Switch, Tree } from 'antd';
import { useDispatch } from 'react-redux';
import classNames from 'classnames';
import type { TreeDataNode } from 'antd';
import { ReactComponent as FolderSvg } from '@assets/icons/folder.svg';
import { ReactComponent as PrivateFolderSvg } from '@assets/icons/folder-private.svg';
import { useCampaignsListWithFoldersQuery } from '@hooks/queries/campaign';
import {
  toggleCampaignFolderCreationModal,
  toggleCopyModal
} from '@redux/actions/modalAction';
import { useCurrentWorkspace } from '@hooks/workspace';
import { ReactComponent as CrossSvg } from '@assets/icons/cross.svg';
import { ReactComponent as SearchSvg } from '@assets/icons/search.svg';
import { ReactComponent as PlusSvg } from '@assets/icons/plus.svg';
import { ReactComponent as MenuSecondarySvg } from '@assets/icons/menu-secondary.svg';

import { useCopyAssetsMutation } from '@hooks/queries/assets';

import './CopyModal.less';

type FormValues = {
  selectedCampaignId: string;
  selectedFolderId: string | null;
  searchText: string;
  copyComments: boolean;
};

type CopyModalAssetsProps = {
  visible: boolean;
  assetIds: string[];
};

export default function CopyModalAssets(props: CopyModalAssetsProps) {
  const { visible, assetIds } = props;
  const [currentWorkspace] = useCurrentWorkspace(false);
  const dispatch = useDispatch();
  const [form] = Form.useForm<FormValues>();
  const canCreateCampaign = currentWorkspace?.permissions?.createCampaigns;

  const searchText = Form.useWatch('searchText', form) ?? '';
  const selectedCampaignId = Form.useWatch('selectedCampaignId', form) ?? '';
  const selectedFolderId = Form.useWatch('selectedFolderId', form) ?? '';
  const copyComments = Form.useWatch('copyComments', form) ?? false;

  const { data: campaignsListQuery } = useCampaignsListWithFoldersQuery({
    params: {
      workspaceId: currentWorkspace?.id || ''
    }
  });

  const onCreateFolder = useCallback(
    (campaignId: string) => {
      if (!campaignId) return;
      dispatch(
        toggleCampaignFolderCreationModal({
          visible: true,
          entity: 'folder',
          campaignId
        })
      );
    },
    [dispatch]
  );

  const onCreateProject = useCallback(() => {
    if (!canCreateCampaign) return;
    dispatch(
      toggleCampaignFolderCreationModal({
        visible: true,
        entity: 'campaign',
        notShowCampaignCreatedModal: true
      })
    );
  }, [dispatch, canCreateCampaign]);

  const campaignsList: TreeDataNode[] = useMemo(() => {
    let _campaignsList = campaignsListQuery ?? [];
    if (searchText) {
      _campaignsList = _campaignsList.filter((campaign) => {
        return campaign.name
          .toLocaleLowerCase()
          .includes(searchText.toLocaleLowerCase());
      });
    }
    return _campaignsList.map((item) => ({
      key: item.id,
      title: item.name,
      icon: (
        <MenuSecondarySvg className="b-icon-campaign" width={16} height={16} />
      ),
      children: item.folders?.length
        ? [
            ...item.folders.map((folder, index, array) => ({
              icon: folder.isPrivate ? (
                <PrivateFolderSvg width={20} height={20} />
              ) : (
                <FolderSvg width={20} height={20} />
              ),
              key: folder.id,
              campaignId: item.id,
              selectable: item.canUploadToRoot,
              className:
                array.length - 1 === index ? 'b-folder-item-last' : undefined,
              title: folder.name
            })),
            {
              key: 'new-folder',
              title: 'New folder',
              campaignId: item.id,
              className: 'new-folder-item',
              icon: <PlusSvg className="b-plus-icon" />
            }
          ]
        : undefined
    }));
  }, [campaignsListQuery, searchText]);

  const { mutateAsync: copyAssets, isPending: isCopyingAssets } =
    useCopyAssetsMutation({});

  const onCloseModal = useCallback(() => {
    dispatch(toggleCopyModal(null));
  }, [dispatch]);

  const onCopyAssets = (data: FormValues) => {
    copyAssets(
      {
        workspaceId: currentWorkspace?.id || '',
        assetIds,
        targetCampaignId: data.selectedCampaignId,
        targetFolderId: data.selectedFolderId ?? undefined,
        copyComments: data.copyComments
      },
      {
        onSuccess: (res) => {
          if (res.data.jobId) {
            localStorage.setItem(
              `copy-link-${res.data.jobId}`,
              `/campaigns/single/${data.selectedCampaignId}/${
                data.selectedFolderId ?? ''
              }`
            );
          }
          onCloseModal();
        }
      }
    );
  };

  return (
    <Modal
      open={visible}
      footer={null}
      centered={true}
      width={656}
      destroyOnClose
      zIndex={1000}
      closeIcon={<CrossSvg />}
      bodyStyle={{ maxHeight: 'calc(100vh - 30px)', height: 'auto' }}
      onCancel={onCloseModal}
      className={classNames('copy-modal')}
    >
      <Form
        form={form}
        onFinish={onCopyAssets}
        validateTrigger="onChange"
        layout="vertical"
        initialValues={{
          copyComments: true
        }}
      >
        <h2
          className="modal_container__title"
          style={{ fontSize: 22, marginBottom: 24 }}
        >
          Copy to
        </h2>
        <Form.Item name="searchText" className="copy-modal__search-input">
          <Input
            placeholder="Search by project"
            prefix={<SearchSvg />}
            size="large"
          />
          {canCreateCampaign && (
            <Button
              htmlType="button"
              type="ghost"
              className="copy-modal__action-button"
              onClick={onCreateProject}
              icon={<PlusSvg className="b-plus-icon" />}
            >
              New project
            </Button>
          )}
        </Form.Item>
        <div className="copy-modal__tree-wrapper">
          <Tree
            treeData={campaignsList}
            showIcon={true}
            selectedKeys={
              selectedFolderId ? [selectedFolderId] : [selectedCampaignId]
            }
            onSelect={(_selectedKeys, { node }) => {
              const hasCampaignId = 'campaignId' in node;
              if (node.key === 'new-folder') {
                onCreateFolder(
                  hasCampaignId ? (node.campaignId as string) : ''
                );
                return;
              }
              if (node.selected) {
                form.setFieldsValue({
                  selectedCampaignId: '',
                  selectedFolderId: ''
                });
                return;
              }
              if (hasCampaignId) {
                form.setFieldsValue({
                  selectedCampaignId: node.campaignId as string,
                  selectedFolderId: node.key as string
                });
                return;
              }
              form.setFieldsValue({
                selectedCampaignId: node.key as string,
                selectedFolderId: null
              });
            }}
          />
        </div>
        <Form.Item name="selectedCampaignId" hidden>
          <Input size="large" />
        </Form.Item>
        <Form.Item name="selectedFolderId" hidden>
          <Input size="large" />
        </Form.Item>
        <Form.Item name="copyComments" valuePropName="checked" hidden />
        <div className="modal_container__actions">
          <div
            className="copy-modal__switch"
            onClick={() => form.setFieldValue('copyComments', !copyComments)}
          >
            <Switch checked={!!copyAssets} />
            <span>Copy comments</span>
          </div>
          <Button
            type="text"
            onClick={onCloseModal}
            style={{
              marginRight: 12,
              border: 'none',
              height: 48,
              padding: '12px 19px'
            }}
          >
            Cancel
          </Button>
          <Button
            type="primary"
            size="large"
            loading={isCopyingAssets}
            disabled={!selectedCampaignId}
            htmlType="submit"
            style={{ width: 152, height: 48, padding: '8px 12px' }}
          >
            Copy {assetIds?.length ?? ''} items
          </Button>
        </div>
      </Form>
    </Modal>
  );
}
